import { createApi } from "@reduxjs/toolkit/query/react";
import { publicBaseQuery } from "@/api/base/public/publicBaseQuery";

function getLocaleHeader(): string {
  if (typeof document === "undefined") return "en";
  const m = document.cookie.match(/(?:^|;\s*)NEXT_LOCALE=([^;]+)/);
  return m?.[1] ? decodeURIComponent(m[1]) : "en";
}

export type RegisterBySlugPayload = {
  invite_code: string;
  firstname?: string | null;
  lastname?: string | null;
  patronymic?: string | null;
  password?: string | null;
  recaptcha_token?: string | null;
  /* Generic flow sends 0/1; the daryny flow sends a real boolean, as the old front did. */
  agree?: 0 | 1 | boolean;

  /**
   * Registrant contact (phone was replaced by email).
   */
  email?: string | null;

  /**
   * Usually used as "username" (e.g. IIN).
   * Example from backend:
   *   "username":"031217500407"
   */
  username?: string | null;

  /**
   * Dynamic fields (example from backend):
   *   astana_daryn_school_id
   *   astana_daryn_teacher_name
   *   astana_daryn_class_name
   *
   * We keep it flexible because the prefix depends on {slug}.
   */
  [key: string]: unknown;
};

export type RegisterBySlugResponse = unknown;

/**
 * Some invite flavours are served by their own controller group:
 *   almaty-daryn -> /api/almaty-daryn/register/{validate,submit}.json
 * `null` keeps the generic /api/register/{validate,submit}.json endpoints.
 */
export type RegisterApiGroup = string | null;

function registerUrl(apiGroup: RegisterApiGroup, action: "validate" | "submit"): string {
  const group = (apiGroup ?? "").trim();
  return group ? `/api/${group}/register/${action}.json` : `/api/register/${action}.json`;
}

export const registerBySlugPublicApi = createApi({
  reducerPath: "registerBySlugPublicApi",
  baseQuery: publicBaseQuery(),
  endpoints: (builder) => ({
    validateRegisterBySlug: builder.mutation<
      RegisterBySlugResponse,
      { apiGroup?: RegisterApiGroup; payload: RegisterBySlugPayload }
    >({
      query: ({ apiGroup = null, payload }) => ({
        url: registerUrl(apiGroup, "validate"),
        method: "POST",
        data: payload,
        headers: { locale: getLocaleHeader() },
      }),
    }),

    submitRegisterBySlug: builder.mutation<
      RegisterBySlugResponse,
      { apiGroup?: RegisterApiGroup; payload: RegisterBySlugPayload }
    >({
      query: ({ apiGroup = null, payload }) => ({
        url: registerUrl(apiGroup, "submit"),
        method: "POST",
        data: payload,
        headers: { locale: getLocaleHeader() },
      }),
    }),
  }),
});

export const { useValidateRegisterBySlugMutation, useSubmitRegisterBySlugMutation } =
  registerBySlugPublicApi;
